import React, { useEffect, useState } from 'react';
import { AdminToggleRow } from './admin-toggle-row';
import {
  ChatsEmployees_UpdateShowAppPaymentsTabMutation,
  ChatsEmployees_UpdateShowAppPaymentsTabMutationVariables,
  EmployeeDetail_UpdateEmployeeRoleMutation,
  EmployeeDetail_UpdateEmployeeRoleMutationVariables,
  EmployeeRole,
  RestaurantEmployeeFragment,
} from '@/types';
import { createStyles } from '@/contexts/theme-context';
import { gql, useMutation } from '@apollo/client';
import { LIST_ITEM_SEPARATOR_HEIGHT } from '@/styles';
import { ShowPaymentsTabToggleRow } from './show-payments-tab-toggle-row';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateCachedRestaurantEmployee } from '@/hooks/use-restaurant-employees';
import { useUserContext } from '@/contexts/user-context';
import { View } from 'react-native';

export const UPDATE_EMPLOYEE_ROLE = gql`
  mutation EmployeeDetail_UpdateEmployeeRole(
    $businessEmployeeUUID: uuid!
    $employeeRole: String!
  ) {
    update_businesses_employees_by_pk(
      pk_columns: { uuid: $businessEmployeeUUID }
      _set: { employee_role: $employeeRole }
    ) {
      employee_role
      uuid
    }
  }
`;

export const UPDATE_SHOW_PAYMENTS_TAB = gql`
  mutation ChatsEmployees_UpdateShowAppPaymentsTab(
    $chatEmployeeUUID: uuid!
    $showAppPaymentsTab: Boolean!
  ) {
    update_chats_employees_by_pk(
      pk_columns: { uuid: $chatEmployeeUUID }
      _set: { show_app_payments_tab: $showAppPaymentsTab }
    ) {
      show_app_payments_tab
      uuid
    }
  }
`;

interface Props {
  businessEmployee: RestaurantEmployeeFragment;
  onUpdate: (updatedEmployee: Partial<RestaurantEmployeeFragment>) => void;
}

const FullWidthSeparator: React.FC = () => {
  const [styles] = useStyles();
  return <View style={styles.fullWidthSeparator} />;
};

export const UpdateEmployeeFooter: React.FC<Props> = ({
  businessEmployee,
  onUpdate,
}) => {
  const updateCachedRestaurantEmployee = useUpdateCachedRestaurantEmployee();
  const { selectedRestaurantRelationship } = useUserContext();
  const { showErrorToast } = useToasts();
  const [styles] = useStyles();

  const [isEmployeeAdmin, setIsEmployeeAdmin] = useState(
    businessEmployee.restaurant_employee_role === EmployeeRole.Admin,
  );
  const [employeeShowAppPaymentsTab, setEmployeeShowAppPaymentsTab] = useState(
    businessEmployee.show_app_payments_tab,
  );

  useEffect(() => {
    setIsEmployeeAdmin(
      businessEmployee.restaurant_employee_role === EmployeeRole.Admin,
    );
    setEmployeeShowAppPaymentsTab(businessEmployee.show_app_payments_tab);
  }, [businessEmployee]);

  const [updateBusinessEmployeeRole] = useMutation<
    EmployeeDetail_UpdateEmployeeRoleMutation,
    EmployeeDetail_UpdateEmployeeRoleMutationVariables
  >(UPDATE_EMPLOYEE_ROLE, {
    update: (client, { data }) => {
      const updatedRestaurantEmployee = data?.update_businesses_employees_by_pk;
      if (updatedRestaurantEmployee) {
        const updatedEmployee: Partial<RestaurantEmployeeFragment> = {
          restaurant_employee_role:
            updatedRestaurantEmployee.employee_role as EmployeeRole,
        };

        updateCachedRestaurantEmployee(client, (cachedRestaurantEmployees) => {
          return cachedRestaurantEmployees.map((re) => {
            return re.restaurant_employee_id === updatedRestaurantEmployee.uuid
              ? {
                  ...re,
                  ...updatedEmployee,
                }
              : re;
          });
        });

        onUpdate(updatedEmployee);
      }
    },
  });

  const [updateShowAppPaymentsFlag] = useMutation<
    ChatsEmployees_UpdateShowAppPaymentsTabMutation,
    ChatsEmployees_UpdateShowAppPaymentsTabMutationVariables
  >(UPDATE_SHOW_PAYMENTS_TAB, {
    update: (client, { data }) => {
      const updatedChatEmployee = data?.update_chats_employees_by_pk;
      if (updatedChatEmployee) {
        const updatedEmployee: Partial<RestaurantEmployeeFragment> = {
          show_app_payments_tab: updatedChatEmployee.show_app_payments_tab!,
        };

        updateCachedRestaurantEmployee(client, (cachedRestaurantEmployees) => {
          return cachedRestaurantEmployees.map((re) => {
            return re.chat_employee_id === updatedChatEmployee.uuid
              ? {
                  ...re,
                  ...updatedEmployee,
                }
              : re;
          });
        });

        onUpdate(updatedEmployee);
      }
    },
  });

  return (
    <>
      <FullWidthSeparator />
      <AdminToggleRow
        listItemStyle={styles.listItem}
        onValueChange={async (isAdmin) => {
          setIsEmployeeAdmin(isAdmin);
          try {
            await updateBusinessEmployeeRole({
              variables: {
                businessEmployeeUUID: businessEmployee.restaurant_employee_id,
                employeeRole: isAdmin ? EmployeeRole.Admin : EmployeeRole.User,
              },
            });
          } catch (e) {
            setIsEmployeeAdmin(!isAdmin);
            showErrorToast();
          }
        }}
        testID="admin-switch"
        trackingData={{
          name: 'business_employee_admin_switch',
          properties: {
            value: !isEmployeeAdmin,
          },
        }}
        value={isEmployeeAdmin}
      />
      {selectedRestaurantRelationship.appPaymentsEnabled ? (
        <>
          <FullWidthSeparator />
          <ShowPaymentsTabToggleRow
            listItemStyle={styles.listItem}
            onValueChange={async (showAppPayments) => {
              setEmployeeShowAppPaymentsTab(showAppPayments);
              try {
                await updateShowAppPaymentsFlag({
                  variables: {
                    chatEmployeeUUID: businessEmployee.chat_employee_id,
                    showAppPaymentsTab: showAppPayments,
                  },
                });
              } catch (e) {
                setEmployeeShowAppPaymentsTab(!showAppPayments);
                showErrorToast();
              }
            }}
            trackingData={{
              name: 'show_app_payments_tab_switch',
              properties: {
                value: !employeeShowAppPaymentsTab,
              },
            }}
            value={employeeShowAppPaymentsTab}
          />
        </>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  fullWidthSeparator: {
    backgroundColor: theme.colors.stroke,
    height: LIST_ITEM_SEPARATOR_HEIGHT,
  },
  listItem: {
    paddingHorizontal: 0,
  },
}));
